import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Icon,
  Divider,
  Paper,
  Avatar,
  IconButton,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
} from "@mui/material";
import { Client, ClientStats } from "../../types/super-admin.types";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`business-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface BusinessDetailsProps {
  open: boolean;
  onClose: () => void;
  business: Client | null;
  stats: ClientStats | null;
  isLoading?: boolean;
}

const StatsCard = ({ icon, title, value, subtitle, color }: any) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      height: "100%",
      borderRadius: 2,
      bgcolor: "background.paper",
      border: 1,
      borderColor: "divider",
      position: "relative",
      overflow: "hidden",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: `${color}.main`,
        "& .icon-wrapper": {
          transform: "scale(1.1)",
        },
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "4px",
        bgcolor: `${color}.main`,
      },
    }}
  >
    <Box
      sx={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: "text.primary",
              fontWeight: 700,
              mb: 1,
              fontSize: "1.75rem",
            }}
          >
            {value}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {subtitle}
          </Typography>
        </Box>
        <Box
          className="icon-wrapper"
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: `${color}.50`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 0.2s ease",
          }}
        >
          <Icon sx={{ color: `${color}.main`, fontSize: "1.5rem" }}>
            {icon}
          </Icon>
        </Box>
      </Box>
      <Box
        sx={{
          mt: "auto",
          pt: 2,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: `${color}.main`,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {title}
          <Icon sx={{ fontSize: "1rem" }}>arrow_right</Icon>
        </Typography>
      </Box>
    </Box>
  </Paper>
);

export const BusinessDetails: React.FC<BusinessDetailsProps> = ({
  open,
  onClose,
  business,
  stats,
  isLoading,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  if (!business) return null;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleUserMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    userId: string
  ) => {
    setUserMenuAnchor(event.currentTarget);
    setSelectedUser(userId);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
    setSelectedUser(null);
  };

  // Mock user data - replace with actual data later
  const mockUsers = [
    {
      id: "1",
      name: "Business Admin",
      email: business.owner.email,
      role: "BUSINESS_ADMIN",
      lastActive: new Date().toISOString(),
      status: "active",
    },
    {
      id: "2",
      name: "John Cashier",
      email: "john@example.com",
      role: "CASHIER",
      lastActive: new Date().toISOString(),
      status: "active",
    },
    {
      id: "3",
      name: "Sarah Manager",
      email: "sarah@example.com",
      role: "MANAGER",
      lastActive: "2023-12-25T10:30:00",
      status: "inactive",
    },
  ];

  const getStorageUsagePercentage = () => {
    const maxStorage = 100; // GB
    const used = Math.round((stats?.storageUsed || 0) / 1024);
    return (used / maxStorage) * 100;
  };

  const getUserStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "80vh",
          bgcolor: "background.default",
          boxShadow: (theme) => theme.shadows[3],
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "primary.main",
                fontSize: "1.5rem",
                fontWeight: 600,
              }}
            >
              {business.business_name.charAt(0)}
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{ color: "text.primary", fontWeight: 600, mb: 0.5 }}
              >
                {business.business_name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Icon sx={{ fontSize: 16 }}>business</Icon>
                ID: {business.id}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={business.is_active ? "Active" : "Inactive"}
            color={business.is_active ? "success" : "error"}
            sx={{
              fontWeight: 500,
              height: 32,
              borderRadius: 2,
              px: 1,
              "& .MuiChip-label": { px: 1.5 },
            }}
          />
        </Box>
      </DialogTitle>

      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            px: 3,
            "& .MuiTab-root": {
              minHeight: 56,
              fontSize: "0.875rem",
              fontWeight: 500,
              textTransform: "none",
              color: "text.secondary",
              "&.Mui-selected": {
                color: "primary.main",
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab
            label="Overview"
            icon={<Icon>dashboard</Icon>}
            iconPosition="start"
          />
          <Tab label="Users" icon={<Icon>group</Icon>} iconPosition="start" />
          <Tab
            label="Settings"
            icon={<Icon>settings</Icon>}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ bgcolor: "background.default", p: 3 }}>
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatsCard
                    icon="shopping_cart"
                    title="Transactions"
                    value={stats?.totalTransactions?.toLocaleString() || 0}
                    subtitle="Total transactions processed"
                    color="primary"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatsCard
                    icon="payments"
                    title="Revenue"
                    value={`$${stats?.monthlyRevenue?.toLocaleString() || 0}`}
                    subtitle="Monthly revenue generated"
                    color="success"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatsCard
                    icon="group"
                    title="Users"
                    value={stats?.activeUsers || 0}
                    subtitle="Active system users"
                    color="warning"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatsCard
                    icon="storage"
                    title="Storage"
                    value={`${Math.round((stats?.storageUsed || 0) / 1024)} GB`}
                    subtitle={`${getStorageUsagePercentage()}% of 100 GB used`}
                    color="info"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Business Details Card */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "text.primary", fontWeight: 600, mb: 3 }}
                >
                  Business Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography color="text.secondary" gutterBottom>
                      Owner
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "text.primary", fontWeight: 500 }}
                    >
                      {business.owner.first_name} {business.owner.last_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color="text.secondary" gutterBottom>
                      Contact
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "text.primary", fontWeight: 500 }}
                    >
                      {business.owner.email}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "text.primary", fontWeight: 500 }}
                    >
                      {business.owner.phone || "No phone provided"}
                    </Typography>
                  </Grid>
                  {business.contact && (
                    <Grid item xs={12}>
                      <Typography color="text.secondary" gutterBottom>
                        Address
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "text.primary", fontWeight: 500 }}
                      >
                        {business.contact.address}
                        {business.contact.city && `, ${business.contact.city}`}
                        {business.contact.state &&
                          `, ${business.contact.state}`}
                        {business.contact.postal_code &&
                          ` ${business.contact.postal_code}`}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Subscription Details Card */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "text.primary", fontWeight: 600, mb: 3 }}
                >
                  Subscription Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                      <Chip
                        label={business.subscription.plan.toUpperCase()}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 500, borderRadius: 1 }}
                      />
                      <Chip
                        label={business.subscription.status}
                        color={
                          business.subscription.status === "active"
                            ? "success"
                            : "warning"
                        }
                        size="small"
                        sx={{ fontWeight: 500, borderRadius: 1 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography color="text.secondary" gutterBottom>
                      Start Date
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "text.primary", fontWeight: 500 }}
                    >
                      {new Date(
                        business.subscription.start_date
                      ).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography color="text.secondary" gutterBottom>
                      End Date
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "text.primary", fontWeight: 500 }}
                    >
                      {new Date(
                        business.subscription.end_date
                      ).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color="text.secondary" gutterBottom>
                      Features
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}
                    >
                      <Chip
                        size="small"
                        label={`${business.subscription.features.max_users} Users`}
                        icon={<Icon fontSize="small">group</Icon>}
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        size="small"
                        label={`${business.subscription.features.max_products} Products`}
                        icon={<Icon fontSize="small">inventory_2</Icon>}
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        size="small"
                        label={`${business.subscription.features.max_locations} Locations`}
                        icon={<Icon fontSize="small">store</Icon>}
                        variant="outlined"
                        color="primary"
                      />
                      {business.subscription.features.has_inventory && (
                        <Chip
                          size="small"
                          label="Inventory"
                          icon={<Icon fontSize="small">inventory</Icon>}
                          variant="outlined"
                          color="success"
                        />
                      )}
                      {business.subscription.features.has_analytics && (
                        <Chip
                          size="small"
                          label="Analytics"
                          icon={<Icon fontSize="small">analytics</Icon>}
                          variant="outlined"
                          color="info"
                        />
                      )}
                      {business.subscription.features.has_customer_loyalty && (
                        <Chip
                          size="small"
                          label="Customer Loyalty"
                          icon={<Icon fontSize="small">loyalty</Icon>}
                          variant="outlined"
                          color="warning"
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Users Tab */}
        <TabPanel value={tabValue} index={1}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                p: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "text.primary", fontWeight: 600 }}
              >
                System Users
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon>person_add</Icon>}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Add User
              </Button>
            </Box>
            <Divider />
            <List sx={{ py: 0 }}>
              {mockUsers.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:last-child": { borderBottom: 0 },
                    py: 2,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                      }}
                    >
                      {user.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ color: "text.primary", fontWeight: 600 }}
                        >
                          {user.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={user.status}
                          color={getUserStatusColor(user.status)}
                          sx={{
                            fontWeight: 600,
                            borderRadius: 1,
                            height: 24,
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary" }}
                        >
                          {user.email}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontWeight: 500 }}
                        >
                          Last active:{" "}
                          {new Date(user.lastActive).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => handleUserMenuOpen(e, user.id)}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          bgcolor: "action.hover",
                          color: "primary.main",
                        },
                      }}
                    >
                      <Icon>more_vert</Icon>
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </TabPanel>

        {/* Settings Tab */}
        <TabPanel value={tabValue} index={2}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "text.primary", fontWeight: 600, mb: 2 }}
            >
              Business Settings
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: "text.primary", fontWeight: 600, mb: 2 }}
                >
                  General Settings
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{ color: "text.secondary", fontWeight: 500, mb: 0.5 }}
                  >
                    Timezone
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.primary" }}>
                    {business.settings.timezone}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{ color: "text.secondary", fontWeight: 500, mb: 0.5 }}
                  >
                    Language
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.primary" }}>
                    {business.settings.language}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{ color: "text.secondary", fontWeight: 500, mb: 0.5 }}
                  >
                    Currency
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.primary" }}>
                    {business.settings.currency}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: "text.primary", fontWeight: 600, mb: 2 }}
                >
                  Tax Settings
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{ color: "text.secondary", fontWeight: 500, mb: 0.5 }}
                  >
                    Tax Included in Prices
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.primary" }}>
                    {business.settings.tax_settings.tax_included ? "Yes" : "No"}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{ color: "text.secondary", fontWeight: 500, mb: 0.5 }}
                  >
                    Default Tax Rate
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.primary" }}>
                    {business.settings.tax_settings.default_tax_rate}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </TabPanel>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<Icon>close</Icon>}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 500,
            borderColor: "divider",
          }}
        >
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          startIcon={<Icon>edit</Icon>}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 500,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          Edit Business
        </Button>
      </DialogActions>

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: 2,
            borderRadius: 2,
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={handleUserMenuClose}
          sx={{
            py: 1,
            "&:hover": { bgcolor: "primary.50" },
          }}
        >
          <Icon sx={{ mr: 1, color: "primary.main" }}>edit</Icon>
          <Typography sx={{ color: "text.primary", fontWeight: 500 }}>
            Edit User
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleUserMenuClose}
          sx={{
            py: 1,
            "&:hover": { bgcolor: "warning.50" },
          }}
        >
          <Icon sx={{ mr: 1, color: "warning.main" }}>key</Icon>
          <Typography sx={{ color: "text.primary", fontWeight: 500 }}>
            Reset Password
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleUserMenuClose}
          sx={{
            py: 1,
            "&:hover": { bgcolor: "error.50" },
          }}
        >
          <Icon sx={{ mr: 1, color: "error.main" }}>block</Icon>
          <Typography sx={{ color: "text.primary", fontWeight: 500 }}>
            Disable Account
          </Typography>
        </MenuItem>
      </Menu>
    </Dialog>
  );
};
